import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndianRupeeSign } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const RPCalculator = () => {
    const [currentAge, setCurrentAge] = useState('');
    const [retirementAge, setRetirementAge] = useState('');
    const [currentMonthlyExpense, setCurrentMonthlyExpense] = useState('');
    const [expectedInflationRate, setExpectedInflationRate] = useState('');
    const [currentSavings, setCurrentSavings] = useState('');
    const [expectedReturnOnSavings, setExpectedReturnOnSavings] = useState('');
    const [totalAmountRequired, setTotalAmountRequired] = useState(null);
    const [currentSavingsGrowth, setCurrentSavingsGrowth] = useState(null);
    const [additionalAmountRequired, setAdditionalAmountRequired] = useState(null);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    };

    const calculateRP = () => {
        const CA = parseFloat(currentAge);
        const RA = parseFloat(retirementAge);
        const CME = parseFloat(currentMonthlyExpense);
        const EIR = parseFloat(expectedInflationRate) / 100;
        const CS = parseFloat(currentSavings);
        const ROR = parseFloat(expectedReturnOnSavings) / 100;

        if (CA <= 0 || RA <= 0 || CME <= 0 || EIR < 0 || CS < 0 || ROR < 0 || CA >= RA) {
            setTotalAmountRequired(null);
            setCurrentSavingsGrowth(null);
            setAdditionalAmountRequired(null);
            return;
        }

        const yearsToRetirement = RA - CA;
        const retirementPeriod = 25;
        const futureMonthlyExpense = CME * Math.pow(1 + EIR, yearsToRetirement);
        const futureAnnualExpense = futureMonthlyExpense * 12;
        let projectedRetirementCorpus = 0;
        let effectiveReturn = (1 + ROR) / (1 + EIR) - 1;

        if (effectiveReturn > 0) {
            projectedRetirementCorpus = (futureAnnualExpense / effectiveReturn) * (1 - Math.pow(1 + effectiveReturn, -retirementPeriod));
        } else if (effectiveReturn === 0) {
            projectedRetirementCorpus = futureAnnualExpense * retirementPeriod;
        } else {
            projectedRetirementCorpus = futureAnnualExpense * retirementPeriod;
        }

        const calculatedCurrentSavingsGrowth = CS * Math.pow(1 + ROR, yearsToRetirement);
        const calculatedAdditionalAmountRequired = projectedRetirementCorpus - calculatedCurrentSavingsGrowth;

        setTotalAmountRequired(projectedRetirementCorpus.toFixed(2));
        setCurrentSavingsGrowth(calculatedCurrentSavingsGrowth.toFixed(2));
        setAdditionalAmountRequired(Math.max(0, calculatedAdditionalAmountRequired).toFixed(2));
    };

    return (
        <div>
            <Helmet>
                <title>RP Calculator | Calculate Your Insurance Rate Premium</title>
                <meta name="description" content="Use our RP Calculator to accurately compute your insurance rate premium. Get quick, reliable results to make informed insurance decisions." />
                <link rel="canonical" href="https://policysamjho.com/calculators/rp-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "RP Calculator",
                        "description": "Use our RP Calculator to accurately compute your insurance rate premium. Get quick, reliable results to make informed insurance decisions.",
                        "url": "https://policysamjho.com/calculators/rp-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className="calculator-back-button">
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faIndianRupeeSign} className="calculator-header-icon" />
                            <h1 className="calculator-title">RP Calculator</h1>
                        </div>
                        <p className="calculator-description">
                            Plan in advance for your retirement finances with our retirement planning calculator. Find out how much you need to save for a financially secure future.
                        </p>
                    </div>
                    <div className={`calculator-main-section ${totalAmountRequired === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="currentAge">Your Current Age (Years)</label>
                                    <input
                                        type="number"
                                        id="currentAge"
                                        value={currentAge}
                                        onChange={(e) => setCurrentAge(e.target.value)}
                                        placeholder="e.g. 30"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="retirementAge">Retirement Age (Years)</label>
                                    <input
                                        type="number"
                                        id="retirementAge"
                                        value={retirementAge}
                                        onChange={(e) => setRetirementAge(e.target.value)}
                                        placeholder="e.g. 60"
                                    />
                                </div>
                            </div>
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="currentMonthlyExpense">Current Monthly Expense (₹)</label>
                                    <input
                                        type="number"
                                        id="currentMonthlyExpense"
                                        value={currentMonthlyExpense}
                                        onChange={(e) => setCurrentMonthlyExpense(e.target.value)}
                                        placeholder="e.g. 30000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="expectedInflationRate">Expected Inflation Rate (%)</label>
                                    <input
                                        type="number"
                                        id="expectedInflationRate"
                                        value={expectedInflationRate}
                                        onChange={(e) => setExpectedInflationRate(e.target.value)}
                                        placeholder="e.g. 6"
                                    />
                                </div>
                            </div>
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="currentSavings">Your Current Savings (₹)</label>
                                    <input
                                        type="number"
                                        id="currentSavings"
                                        value={currentSavings}
                                        onChange={(e) => setCurrentSavings(e.target.value)}
                                        placeholder="e.g. 500000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="expectedReturnOnSavings">Expected Return on Savings (%)</label>
                                    <input
                                        type="number"
                                        id="expectedReturnOnSavings"
                                        value={expectedReturnOnSavings}
                                        onChange={(e) => setExpectedReturnOnSavings(e.target.value)}
                                        placeholder="e.g. 8"
                                    />
                                </div>
                            </div>
                            <button onClick={calculateRP} className="calculate-button">Calculate</button>
                        </div>

                        {totalAmountRequired !== null && (
                            <div className="calculator-result-section">
                                <h2>Retirement Corpus Calculation</h2>
                                <div className="result-item total-payment-item">
                                    <span>Total Amount Required for Retirement</span>
                                    <span className="result-value">₹{totalAmountRequired}</span>
                                </div>
                                <div className="result-item">
                                    <span>Current savings will grow to</span>
                                    <span className="result-value">₹{currentSavingsGrowth}</span>
                                </div>
                                <div className="result-item">
                                    <span>Additional Amount Required</span>
                                    <span className="result-value">₹{additionalAmountRequired}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default RPCalculator;